import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  useGetPreferencesQuery,
  useGetTagsQuery,
  useUpdatePreferencesMutation,
} from '@/services/newsApi';
import { TagSelector } from '@/components/news/TagSelector';
import { Loader } from '@/components/common/Loader';
import { useLogtoAuth } from '@/hooks/useLogtoAuth';
import { useHaptics } from '@/hooks/useHaptics';
import { newsScritturaOra } from '@/lib/newsScritturaOra';

/**
 * Follow-topics preferences screen — let the user pick the tags they want
 * to follow in their personalised feed.
 *
 * Auth-gated: unauthenticated users see a login CTA. Ported from
 * mobile-app/app/news-preferences/index.tsx (`feat/news-revised`).
 */
export function NewsPreferences() {
  const navigate = useNavigate();
  const { t } = useTranslation('news');
  const { isAuthenticated, signIn } = useLogtoAuth();
  const haptics = useHaptics();
  // Fuori dalla produzione, verso la QNews di produzione, il PUT non parte (`lib/newsScrittura.ts`): il tasto si spegne col motivo.
  const puoSalvare = newsScritturaOra();

  const {
    data: allTags = [],
    isLoading: isLoadingTags,
    isError: isErrorTags,
    refetch: refetchTags,
  } = useGetTagsQuery({ type: 'category' }, { skip: !isAuthenticated });

  const { data: preferences, isLoading: isLoadingPrefs } = useGetPreferencesQuery(
    undefined,
    { skip: !isAuthenticated },
  );
  const [updatePreferences, { isLoading: isSaving }] = useUpdatePreferencesMutation();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (preferences?.followedTags) {
      setSelectedIds(preferences.followedTags.map((tag) => tag._id));
    }
  }, [preferences]);

  const handleToggle = useCallback((tagId: string) => {
    setSelectedIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    );
  }, []);

  const handleSave = useCallback(async () => {
    haptics.notification('success');
    await updatePreferences({ followedTags: selectedIds });
    navigate(-1);
  }, [selectedIds, updatePreferences, navigate, haptics]);

  if (!isAuthenticated) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-8">
        <p className="text-center text-base text-fg-muted">
          {t('preferences.loginRequired', {
            defaultValue: 'Log in to personalize your news feed',
          })}
        </p>
        <button
          type="button"
          onClick={signIn}
          className="rounded-2xl bg-brand px-6 py-3.5 text-base font-bold text-brand-fg transition-opacity active:opacity-80"
        >
          {t('login.cta', { defaultValue: 'Log in' })}
        </button>
      </div>
    );
  }

  if (isLoadingTags || isLoadingPrefs) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (isErrorTags) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-8">
        <p className="text-center text-base text-fg-muted">
          {t('preferences.errorTitle', { defaultValue: "Couldn't load preferences" })}
        </p>
        <button
          type="button"
          onClick={() => void refetchTags()}
          className="rounded-full bg-bg-secondary px-6 py-3 text-sm font-semibold text-brand transition-opacity active:opacity-80"
        >
          {t('detailPage.errorRetry', { defaultValue: 'Retry' })}
        </button>
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col">
      <div className="flex-1 overflow-y-auto pb-28 pt-4">
        {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. E anche il sottotitolo. */}
        <h1 className="sr-only">
          {t('preferences.heading', { defaultValue: 'Choose your interests' })}
        </h1>
        <p className="sr-only">
          {t('preferences.subtitle', {
            defaultValue: 'Follow topics to personalize your news feed',
          })}
        </p>

        <TagSelector tags={allTags} selectedIds={selectedIds} onToggle={handleToggle} />
      </div>

      <div className="sticky bottom-0 border-t border-border bg-bg/95 px-5 pb-8 pt-4 backdrop-blur">
        {!puoSalvare ? (
          <p className="mb-3 text-center text-sm text-fg-muted">
            {t('preferences.stagingNoSave', {
              defaultValue: "Preferences aren't saved in staging",
            })}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving || !puoSalvare}
          className="block w-full rounded-2xl bg-brand py-3.5 text-base font-bold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-60"
        >
          {isSaving
            ? t('preferences.saving', { defaultValue: 'Saving...' })
            : t('preferences.saveWithCount', {
                count: selectedIds.length,
                defaultValue: 'Save ({{count}} topics)',
              })}
        </button>
      </div>
    </div>
  );
}
